/**
 * studio-stage.js
 * Quản lý dựng sân khấu Studio 360°:
 * - Phông Cyclorama liền mạch uốn cong đón sáng và bóng đổ
 * - Bục tròn gỗ sồi sáng viền kim loại sang trọng và đèn LED âm dịu
 * - Mặt phẳng đón bóng tiếp xúc (Contact Shadow) dùng ShadowMaterial
 * - Lưới GridHelper tinh tế với màu trục đồng nhất, ẩn mặc định ở Studio/Animals
 */

import * as THREE from 'three';

/**
 * Tạo phông cyclorama hình lòng chảo 360° bằng LatheGeometry
 * Profile: sàn phẳng tâm r=0..8m, uốn cong r=8..14m lên cao 6m, và vách đứng lên cao 14m
 * @returns {THREE.Mesh}
 */
function createCyclorama() {
  const points = [];
  // Đoạn sàn phẳng trung tâm
  points.push(new THREE.Vector2(0, 0));
  points.push(new THREE.Vector2(4.0, 0));
  points.push(new THREE.Vector2(8.0, 0));

  // Đoạn bo cong mượt mà góc sàn - tường (cyclorama curve)
  const arcSegments = 12;
  const rStart = 8.0;
  const rEnd = 13.5;
  const hEnd = 5.5;
  for (let i = 1; i <= arcSegments; i++) {
    const t = i / arcSegments;
    const angle = (t * Math.PI) / 2; // 0 -> PI/2
    const r = rStart + (rEnd - rStart) * (1 - Math.cos(angle));
    const y = hEnd * Math.sin(angle);
    points.push(new THREE.Vector2(r, y));
  }

  // Đoạn tường vát nhẹ lên cao
  points.push(new THREE.Vector2(14.5, 9.0));
  points.push(new THREE.Vector2(15.5, 14.0));

  const latheGeo = new THREE.LatheGeometry(points, 48);

  const latheMat = new THREE.MeshStandardMaterial({
    color: 0x242e3d, // Màu xám xanh trung tính (#2a3444 -> #1b2230)
    roughness: 0.9,
    metalness: 0.05,
    side: THREE.DoubleSide
  });

  const cycloramaMesh = new THREE.Mesh(latheGeo, latheMat);
  cycloramaMesh.name = 'Cyclorama';
  cycloramaMesh.receiveShadow = true;
  return cycloramaMesh;
}

/**
 * Tạo bục tròn gỗ sồi sáng với viền kim loại, đèn LED âm và đĩa nhận bóng thật
 * @returns {THREE.Group}
 */
function createPedestal() {
  const pedestalGroup = new THREE.Group();
  pedestalGroup.name = 'PedestalGroup';

  const pedestalRadius = 1.2;
  const pedestalHeight = 0.08;

  // 1. Thân bục gỗ sồi sáng
  const baseGeo = new THREE.CylinderGeometry(pedestalRadius, pedestalRadius + 0.04, pedestalHeight, 48);
  const woodMat = new THREE.MeshStandardMaterial({
    color: '#c8a27a', // Gỗ sồi sáng
    roughness: 0.65,
    metalness: 0.08
  });
  const pedestalBase = new THREE.Mesh(baseGeo, woodMat);
  pedestalBase.position.y = pedestalHeight / 2; // 0.04 -> đỉnh bục ở 0.08
  pedestalBase.receiveShadow = true;
  pedestalGroup.add(pedestalBase);

  // 2. Viền kim loại mạ vàng sang trọng (openEnded để triệt tiêu hoàn toàn z-fighting mặt trên)
  const rimHeight = 0.02;
  const rimGeo = new THREE.CylinderGeometry(
    pedestalRadius + 0.02,
    pedestalRadius + 0.03,
    rimHeight,
    48,
    1,
    true // openEnded: chỉ là vành tròn, không có nắp trên/dưới gây z-fighting
  );
  const goldMat = new THREE.MeshStandardMaterial({
    color: '#f59e0b',
    metalness: 0.85,
    roughness: 0.25,
    side: THREE.DoubleSide
  });
  const rimMesh = new THREE.Mesh(rimGeo, goldMat);
  // Đỉnh viền ở y = 0.075 (thấp hơn mặt bục 0.08 ít nhất 0.005)
  rimMesh.position.y = 0.075 - rimHeight / 2;
  pedestalGroup.add(rimMesh);

  // 3. Dải đèn LED âm phát sáng dịu nhẹ (Underglow LED)
  const ledGeo = new THREE.CylinderGeometry(pedestalRadius - 0.04, pedestalRadius - 0.04, 0.012, 48, 1, true);
  const ledMat = new THREE.MeshBasicMaterial({
    color: '#38bdf8',
    transparent: true,
    opacity: 0.45,
    side: THREE.DoubleSide
  });
  const ledMesh = new THREE.Mesh(ledGeo, ledMat);
  ledMesh.position.y = 0.007;
  pedestalGroup.add(ledMesh);

  return pedestalGroup;
}

/**
 * Khởi tạo toàn bộ sân khấu Studio:
 * - cyclorama 360
 * - bục pedestal
 * - grid helper tinh tế
 * @param {THREE.Scene} scene
 * @param {object} state
 * @returns {{ studioEnvironmentGroup: THREE.Group, pedestalGroup: THREE.Group, gridHelper: THREE.GridHelper }}
 */
export function setupStudioStage(scene, state) {
  const studioEnvironmentGroup = new THREE.Group();
  studioEnvironmentGroup.name = 'StudioEnvironmentGroup';
  scene.add(studioEnvironmentGroup);

  // Cyclorama phông cong
  const cyclorama = createCyclorama();
  studioEnvironmentGroup.add(cyclorama);

  // Bục tròn pedestal
  const pedestalGroup = createPedestal();
  studioEnvironmentGroup.add(pedestalGroup);

  // GridHelper: đổi màu đường trục trung tâm trùng màu lưới, giảm opacity và ẩn ở Studio/Animals
  const gridHelper = new THREE.GridHelper(30, 30, 0x334155, 0x243044);
  gridHelper.position.y = 0.005;
  if (gridHelper.material) {
    gridHelper.material.transparent = true;
    gridHelper.material.opacity = 0.35;
  }
  gridHelper.visible = false; // Mặc định ẩn ở Studio
  studioEnvironmentGroup.add(gridHelper);

  // Ghi nhận vào state
  state.studioEnvironmentGroup = studioEnvironmentGroup;
  state.pedestalGroup = pedestalGroup;
  state.gridHelper = gridHelper;

  return { studioEnvironmentGroup, pedestalGroup, gridHelper };
}
